import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  makeStyles
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import WorkIcon from '@material-ui/icons/Work';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(2),
    maxWidth: 600,
  },
  dialogTitle: {
    textAlign: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 56,
    color: theme.palette.error.main,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.8rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  jobTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
  },
  warningBox: {
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: 'transparent',
  },
  warningItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  warningItemIcon: {
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  warningText: {
    fontSize: '1rem',
    color: theme.palette.error.dark,
    fontWeight: 600,
  },
  alternativesList: {
    textAlign: 'left',
    margin: theme.spacing(2, 'auto', 3),
    maxWidth: 400,
  },
  alternativeItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
  },
  alternativeIcon: {
    marginRight: theme.spacing(1.5),
    fontSize: 24,
    color: theme.palette.info.main,
  },
  alternativeText: {
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  highlight: {
    fontWeight: 700,
    color: theme.palette.info.main,
  },
  keepButton: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 14px rgba(33,150,243,0.39)',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(33,150,243,0.5)',
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.3s ease',
    marginTop: theme.spacing(2),
  },
  deleteButton: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
    border: '1px solid transparent',
    '&:hover': {
      border: `1px solid ${theme.palette.error.main}`,
      backgroundColor: 'transparent',
    },
  },
  deactivateButton: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const DeleteJobConfirmationDialog = ({ open, onKeepJob, onDeactivateJob, onDeleteJob, jobTitle, isActive }) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onKeepJob} // Default close action is to keep the job
      aria-labelledby="delete-job-confirmation-dialog-title"
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="delete-job-confirmation-dialog-title" className={classes.dialogTitle} disableTypography>
        <Typography variant="h4" className={classes.title} component="div">
          Wait! Are You Sure?
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <div className={classes.iconContainer}>
          <WarningIcon className={classes.warningIcon} />
        </div>

        <DialogContentText variant="h6" color="textPrimary" style={{ fontWeight: 600, marginBottom: 16 }}>
          You're about to permanently delete this job:
        </DialogContentText>

        <Typography className={classes.jobTitle}>
          "{jobTitle}"
        </Typography>

        <Box className={classes.warningBox}>
          <div className={classes.warningItem}>
            <TrendingDownIcon className={classes.warningItemIcon} />
            <Typography className={classes.warningText}>
              This action CANNOT be undone!
            </Typography>
          </div>
          
          <div className={classes.warningItem}>
            <TrendingDownIcon className={classes.warningItemIcon} />
            <Typography className={classes.warningText}>
              All applicant data will be lost forever
            </Typography>
          </div>
          
          <div className={classes.warningItem}>
            <TrendingDownIcon className={classes.warningItemIcon} />
            <Typography className={classes.warningText}>
              You'll lose your payment investment
            </Typography>
          </div>
        </Box>

        <DialogContentText variant="body1" color="textSecondary" style={{ marginBottom: 16 }}>
          <strong>Consider these better alternatives:</strong>
        </DialogContentText>

        <div className={classes.alternativesList}>
          <div className={classes.alternativeItem}>
            <PauseCircleOutlineIcon className={classes.alternativeIcon} />
            <Typography className={classes.alternativeText}>
              <span className={classes.highlight}>Deactivate temporarily</span> - Keep your data safe, reactivate anytime
            </Typography>
          </div>
          
          <div className={classes.alternativeItem}>
            <WorkIcon className={classes.alternativeIcon} />
            <Typography className={classes.alternativeText}>
              <span className={classes.highlight}>Edit the posting</span> - Update requirements or description instead
            </Typography>
          </div>
          
          <div className={classes.alternativeItem}>
            <span className={classes.alternativeIcon} role="img" aria-label="hourglass">⏳</span>
            <Typography className={classes.alternativeText}>
              <span className={classes.highlight}>Wait for candidates</span> - Great hires take time to find
            </Typography>
          </div>
        </div>

        <DialogContentText variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          <span role="img" aria-label="light bulb">💡</span> <strong>Pro Tip:</strong> Most successful hires come from job posts that stay active for 2-4 weeks. Don't give up too soon!
        </DialogContentText>
      </DialogContent>

      <DialogActions style={{ justifyContent: 'center', paddingBottom: 24, flexDirection: 'column' }}>
        <Button
          onClick={onKeepJob}
          variant="contained"
          color="primary"
          className={classes.keepButton}
          startIcon={<WorkIcon />}
        >
          Keep My Job Active
        </Button>
        
        {isActive && (
          <Button
            onClick={onDeactivateJob}
            variant="contained"
            className={classes.deactivateButton}
            startIcon={<PauseCircleOutlineIcon />}
          >
            Just Deactivate (Keep Data)
          </Button>
        )}
        
        <Button 
          onClick={onDeleteJob} 
          className={classes.deleteButton}
          size="small"
        >
          Delete permanently anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteJobConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onKeepJob: PropTypes.func.isRequired,
  onDeactivateJob: PropTypes.func.isRequired,
  onDeleteJob: PropTypes.func.isRequired,
  jobTitle: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
};

DeleteJobConfirmationDialog.defaultProps = {
  isActive: false,
};

export default DeleteJobConfirmationDialog;

